import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Error from 'components/Error/Error';
import Loader from 'components/Loader/Loader';
import { globalData } from 'global/js/globalData';
import { isAndroid, getQueryParams } from 'global/js/helpers';
import './Navigation.scss';

export default class Navigation extends Component {
    constructor(props) {
        super(props);
    }

    componentDidMount() {        
        window.addEventListener('online', this.handleOnline);
    }

    componentWillUnmount() {        
        window.removeEventListener('online', this.handleOnline);
    }

    /**
     * Checks the current internet status, and sends the data if
     * there is a connection. Displays an error message if not.
     */
    checkOnlineStatusAndSend = () => {
        const { actions, isSubmit } = this.props;

        if (navigator.onLine) {
            // Shows the loader immediately. Only need this on submit.
            isSubmit ? actions.postData(true) : this.handleClick();
            // No need to send this right away; waits for reducers to do their thing.
            setTimeout(this.sendSurveyData, 500);
        } else {
            if (isSubmit) this.handleOffline();
        }
    }

    /**
     * Checks if we're on a submit page and the data has been posted,
     * and renders the next page if so. Ensures that we don't transition
     * pages before data is finished posting.
     */
    checkSubmissionAndRenderNextPage = () => {
        if (this.props.isSubmit && this.props.isDataPosted) {
            this.updateStatus('IsComplete');
            this.props.history.push(this.props.nextLink);
        }
    }

    /**
     * Called when the back button is clicked. Scrolls back to the top.
     */
    handleClick = () => window.scrollTo(0, 0); 

    /**
     * Called when the device goes online. Updates the global state.
     */
    handleOnline = () => this.props.actions.internetConnectionError(false);

    /**
     * Called when the device goes offline. Updates the global state.
     */
    handleOffline = () => this.props.actions.internetConnectionError(true);

    /**
     * Called when the next button is clicked. Saves the current survey data.
     */
    handleSubmit = (e) => {
        const { answerValue, isNotApplicable, isOverall, isSubmit } = this.props;
        const hasInvalidAnswer = (!isNotApplicable && answerValue === null)

        // Prevent click throughs on submissions until response comes back.
        if (isSubmit || (isOverall && hasInvalidAnswer))
            e.preventDefault();

        // Prevent clicks on overall questions that don't have an answer. 
        if (!isOverall || answerValue !== null) {
            this.updateStatus('IsInProgress');
            this.checkOnlineStatusAndSend();    
        }
    }

    /**
     * Creates a POST request to send our updated survey data to the API.
     */
    sendSurveyData = () => {
        const { locationIndex, individualID, isOverall, isSubmit, prodTourID, isBackupSurvey, surveyData } = this.props;
        const stayData = typeof locationIndex !== 'undefined' ? surveyData.StaySubmissions[locationIndex] : null;
        const token = JSON.parse(localStorage.getItem('token'));
        const overallData = surveyData.OverallSubmission;
        const sendData = isOverall ? overallData : stayData;
        const endPoint = isOverall ? 'overall' : 'stay';

        // Dispatch our send action.
        this.props.actions.sendSurveyData(
            prodTourID, 
            individualID,
            null,
            null, 
            endPoint, 
            this.setSurveyStatus(sendData, isBackupSurvey),
            false, 
            isSubmit,
            token
        );
    }

    /**
     * Sets the souce field of the submission object to reflect the 
     * device that the user is on. And also, the status of the user's 
     * survey progress.
     * 
     * @param {Object} : data
     */
    setSurveyStatus = (data, isBackupSurvey) => {
        const isAndroidOrManual = isAndroid() ? 'Android' : 'Manual';

        // Set the submission source depending on the type of device we are on.
        data.Submission.Source = isBackupSurvey ? 'Link' : isAndroidOrManual;

        // Set the IsComplete flag used to display the check mark, and the
        // IsSubmitted flag used by the server for efficient updates.
        if (this.props.isSubmit) {
            data.Submission.IsComplete = true;
            data.Submission.IsInProgress = false;
            data.Submission.IsSubmitted = true;
        } else {
            data.Submission.IsSubmitted = false;
        }

        return data;
    }

    /**
     * Sets the Status for the given stay or overall section, which allows
     * the correct visual indicator to show for that stay/section.
     *
     * @param {String} status
     */
    updateStatus = (status) => {
        if (this.props.isOverall) {
            this.props.actions.setOverallStatus(status);
        } else {
            this.props.actions.setLocationStatus(status);
        }
    }

    render() {
        this.checkSubmissionAndRenderNextPage();

        const { answerValue, didDirtyPostFail, didPostFail, isDataPosting, 
            isNoInternet, isNotApplicable, isBackupSurvey, isMobilePhone, isSubmit, nextLink, prevLink } = this.props;
        const { apiDirtyPostError, apiPost500error, noInternetConnection } = globalData.errorMessages;
        const defaultButtonText = isSubmit ? 'Submit' : 'Next';
        const nextButtonText = isNoInternet ? globalData.noInternetButtonText : defaultButtonText;
        const nextClickHandler = isNoInternet ? null : this.handleSubmit;
        const nextToLink = isNoInternet ? '/' : nextLink;
        const navigationClasses = classNames('navigation', {
            'is-mobile': isMobilePhone
        });
        const nextClasses = classNames('navigation__button--next', {
            'disabled': answerValue === null && !isNotApplicable,
            'loading': isDataPosting,
            'is-backup': isBackupSurvey
        });

        return (
            <div>
                {isNoInternet && <Error errorText={noInternetConnection} />}

                {didPostFail && !isNoInternet && <Error errorText={apiPost500error} />}

                {didDirtyPostFail && !isNoInternet && <Error errorText={apiDirtyPostError} />}

                <footer className={navigationClasses}>
                    {!isBackupSurvey && (
                        <Link 
                            className='navigation__button--prev' 
                            onClick={this.handleClick}
                            to={prevLink}
                        >
            				Back
            			</Link>
                    )}

                    <Link 
                        className={nextClasses} 
                        onClick={nextClickHandler}
                        to={nextToLink}
                    >
                        {isDataPosting && <Loader />}

                        {!isDataPosting && nextButtonText}
                    </Link>
                </footer>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./application/components/Navigation/Navigation.jsx